import React, { useState, useMemo } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { useHistory, useLocation } from 'react-router-dom';
import { api } from '../../services/api';
import Icon from '../../components/Icon';
import { showToast } from '../../components/Toast';

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
};

const ResetPasswordPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get('token') || '', [location.search]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const heroImage = isDesktop
    ? `${process.env.PUBLIC_URL}/login-bg.webp`
    : `${process.env.PUBLIC_URL}/login-bg-mobile.webp`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Missing reset token. Please request a new reset link.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
      showToast('Password updated', 'success');
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Could not reset password. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen scrollY>
        <div className="fh-landing">
          <div
            className="fh-landing__photo"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="fh-landing__panel">
            <div className="fh-auth__inner">
              <button
                onClick={() => history.push('/login')}
                className="fh-auth__back"
                aria-label="Back to login"
                style={{ display: 'flex', alignItems: 'center', gap: 4, minHeight: 44 }}
              >
                <Icon name="chevron_left" size={20} color="#c5512d" />
                Back
              </button>

              <div className="fh-auth__header">
                <img src="/icon-nobg.png" alt="FamHearth" className="fh-auth__icon" />
                <h1 className="fh-auth__title">{done ? 'Password updated' : 'Set a new password'}</h1>
                <p className="fh-auth__subtitle">
                  {done ? 'You can now sign in with your new password.' : 'Choose a strong password you haven\'t used before.'}
                </p>
              </div>

              {done ? (
                <div className="fh-auth__form">
                  <button
                    type="button"
                    onClick={() => history.push('/login')}
                    className="fh-auth__submit"
                    style={{ minHeight: 44 }}
                  >
                    Sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} aria-label="Reset password form" className="fh-auth__form">
                  <div className="fh-auth__field">
                    <label className="fh-auth__label">New password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="new-password"
                        placeholder="At least 8 characters"
                        className="fh-auth__input"
                        style={{ paddingRight: 48 }}
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        style={{
                          position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                          minWidth: 44, minHeight: 44,
                        }}
                      >
                        <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={20} color="rgba(255,255,255,0.35)" />
                      </button>
                    </div>
                  </div>
                  <div className="fh-auth__field">
                    <label className="fh-auth__label">Confirm new password</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      placeholder="Re-enter your new password"
                      className="fh-auth__input"
                    />
                  </div>
                  {error && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: 'rgba(239,83,80,0.08)',
                      border: '1px solid rgba(239,83,80,0.2)',
                      borderRadius: 12, padding: '10px 14px',
                    }}>
                      <Icon name="warning" size={18} color="#ef5350" />
                      <p style={{ color: '#ef5350', fontSize: 13, fontWeight: 600, margin: 0 }}>{error}</p>
                    </div>
                  )}
                  <button type="submit" disabled={isLoading} className="fh-auth__submit" style={{ minHeight: 44 }}>
                    {isLoading ? (
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <span style={{
                          width: 18, height: 18, borderRadius: '50%',
                          border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#fff',
                          animation: 'fh-spin 0.6s linear infinite',
                          display: 'inline-block',
                        }} />
                        Updating...
                      </span>
                    ) : 'Update password'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ResetPasswordPage;
